import React, { lazy, Suspense, useEffect } from "react";
import { Route, Switch, withRouter } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import PageNotFound from "../Components/PageNotFound";
import ProtectedRoute from "./ProtectedRoute";
import { useSelector } from "react-redux";

//for refreshing the page when lazy fails loading the component
const lazyWithReload = (componentImport) =>
  lazy(async () => {
    const pageHasAlreadyBeenForceRefreshed = JSON.parse(
      window.localStorage.getItem("page-has-been-force-refreshed") || "false"
    );
    try {
      const component = await componentImport();
      window.localStorage.setItem("page-has-been-force-refreshed", "false");
      return component;
    } catch (error) {
      if (!pageHasAlreadyBeenForceRefreshed) {
        // Assuming that the user is not on the latest version of the application.
        // Let's refresh the page immediately.
        window.localStorage.setItem("page-has-been-force-refreshed", "true");
        return window.location.reload();
      }
      // The page has already been reloaded
      // Assuming that user is already using the latest version of the application.
      throw error;
    }
  });

const OrganizationSetupAlert = lazyWithReload(() => import("../Components/Alert/OrganizationSetupAlert"));

// core app

const Dashboard = lazyWithReload(() => import("../Pages/Dashboard/Pages"));
const Organization = lazyWithReload(() => import("../Pages/CoreSetupApp/Organization/Page"));
const Permission = lazyWithReload(() => import("../Pages/CoreSetupApp/BranchPermission/Page"));
const Role = lazyWithReload(() => import("../Pages/CoreSetupApp/Role/Page"));
const BranchRole = lazyWithReload(() => import("../Pages/CoreSetupApp/BranchRole/Page"));

const BranchListing = lazyWithReload(() => import("../Pages/CoreSetupApp/Branch/Page"));
const Department = lazyWithReload(() => import("../Pages/CoreSetupApp/Department/Page"));
const CountryListing = lazyWithReload(() => import("../Pages/CoreSetupApp/Country/Page"));
const BaseDocRuleListing = lazyWithReload(() => import("../Pages/CoreSetupApp/BaseDocRule/Page"));
const CurrencyListing = lazyWithReload(() => import("../Pages/CoreSetupApp/Currency/Page"));
const ProvinceListing = lazyWithReload(() => import("../Pages/CoreSetupApp/Province/Page"));
const DistrictLisitng = lazyWithReload(() => import("../Pages/CoreSetupApp/District/Page"));
const PalikaListing = lazyWithReload(() => import("../Pages/CoreSetupApp/Palika/Page"));
const WardListing = lazyWithReload(() => import("../Pages/CoreSetupApp/Ward/Page"));
const AreaListing = lazyWithReload(() => import("../Pages/CoreSetupApp/Area/Page"));
const DocumentNumberingListing = lazyWithReload(() => import("../Pages/CoreSetupApp/DocumentNumbering/Page"));
const DocumentSeriesListing = lazyWithReload(() => import("../Pages/CoreSetupApp/DocSeries/Page"));
const FreightListing = lazyWithReload(() => import("../Pages/CoreSetupApp/Freight/Page"));

// user setup
const UserListing = lazyWithReload(() => import("../Pages/CoreSetupApp/User/Page"));
const UserGroupListing = lazyWithReload(() => import("../Pages/CoreSetupApp/UserGroup/Page"));
const ChangePassword = lazyWithReload(() => import("../Pages/CoreSetupApp/User/ResetPassword/ChangePassword"));

// item setup
const ItemListing = lazyWithReload(() => import("../Pages/Inventory/Item/Pages"));

// cost center pages
const DimensionListing = lazyWithReload(() => import("../Pages/CostCenterType/Dimension/Pages"));
const CostCenterTypeListing = lazyWithReload(() => import("../Pages/CostCenterType/CostCenterType/Pages"));
const CostCenterListing = lazyWithReload(() => import("../Pages/CostCenterType/CostCenter/Pages"));
// party pages
const CustomerListing = lazyWithReload(() => import("../Pages/Inventory/Customer/Pages"));

const PartyListing = lazyWithReload(() => import("../Pages/Inventory/Supplier/Pages"));
// Acounting System
const GLAccountListing = lazyWithReload(() => import("../Pages/Accounting/GLAccount/Pages"));
const VoucherTypeListing = lazyWithReload(() => import("../Pages/Accounting/VoucherType/Pages"));
const AccountGroupListing = lazyWithReload(() => import("../Pages/Accounting/GLAccountGroup/Pages"));
const VoucherListing = lazyWithReload(() => import("../Pages/Accounting/Voucher/Page"));
const PaymentVoucherListing = lazyWithReload(() => import("../Pages/Accounting/PaymentVoucher/Page"));
const ReciptVoucherListing = lazyWithReload(() => import("../Pages/Accounting/ReciptVoucher/Page"));
const CostCenterReportListing = lazyWithReload(() => import("../Pages/Accounting/CostCenterReport/Pages"));

// purchase management pages
const MaterialRequest = lazyWithReload(() => import("../Pages/Inventory/MaterialRequest/Pages"));

const PurchaseInvoice = lazyWithReload(() => import("../Pages/Inventory/PurchaseAP/ThirdPartyAP/PurchaseInvoice/Page"));
const PurchaseOrder = lazyWithReload(() => import("../Pages/Inventory/PurchaseAP/ThirdPartyAP/PurchaseOrder/Page"));
const PurchaseReturnBranchListing = lazyWithReload(() =>
  import("../Pages/Inventory/PurchaseAP/Interbranch/PurchaseReturnBranch/Page")
);
const PurchaseOrderBranchListing = lazyWithReload(() =>
  import("../Pages/Inventory/PurchaseAP/Interbranch/PurchaseOrderBranch/Page")
);
const PurchaseInvoiceBranchListing = lazyWithReload(() =>
  import("../Pages/Inventory/PurchaseAP/Interbranch/PurchaseInvoiceBranch/Page")
);
const GRNListing = lazyWithReload(() => import("../Pages/Inventory/PurchaseAP/ThirdPartyAP/GRN/Pages"));
const GrnInvoiceListing = lazyWithReload(() => import("../Pages/Inventory/PurchaseAP/ThirdPartyAP/GrnInvoice/Pages"));
const PurchaseReturnListing = lazyWithReload(() =>
  import("../Pages/Inventory/PurchaseAP/ThirdPartyAP/PurchaseReturn/Page")
);
const PurchaseDebitNoteListing = lazyWithReload(() =>
  import("../Pages/Inventory/PurchaseAP/ThirdPartyAP/PurchaseDebitNote/Page")
);
const PurchaseCreditNoteListing = lazyWithReload(() =>
  import("../Pages/Inventory/PurchaseAP/ThirdPartyAP/PurchaseCreditNote/Page")
);

// sale management pages
const SaleOrderRequestListing = lazyWithReload(() =>
  import("../Pages/Inventory/SaleAR/Interbranch/SaleOrderRequest/Page")
);
const SaleInvoiceBranchListing = lazyWithReload(() =>
  import("../Pages/Inventory/SaleAR/Interbranch/SaleInvoiceBranch/Page")
);
const SaleReturnBranchListing = lazyWithReload(() =>
  import("../Pages/Inventory/SaleAR/Interbranch/SaleReturnBranch/Page")
);
const SaleOrderBranchListing = lazyWithReload(() =>
  import("../Pages/Inventory/SaleAR/Interbranch/SaleOrderBranch/Page")
);

// third party
const SaleOrderListing = lazyWithReload(() => import("../Pages/Inventory/SaleAR/ThirdPartyAR/SaleOrder/Page"));
const DeliveryNoteListing = lazyWithReload(() => import("../Pages/Inventory/SaleAR/ThirdPartyAR/DeliveryNote/Page"));
const SaleInoviceListing = lazyWithReload(() => import("../Pages/Inventory/SaleAR/ThirdPartyAR/SaleInvoice/Page"));
const SaleReturnListing = lazyWithReload(() => import("../Pages/Inventory/SaleAR/ThirdPartyAR/SaleReturn/Page"));
const SaleListing = lazyWithReload(() => import("../Pages/Inventory/Sale/Page"));
const SaleDebitNoteListing = lazyWithReload(() => import("../Pages/Inventory/SaleAR/ThirdPartyAR/SaleDebitNote/Page"));
const SaleCreditNoteListing = lazyWithReload(() =>
  import("../Pages/Inventory/SaleAR/ThirdPartyAR/SaleCreditNote/Page")
);

// product pages
const BaseUnitListing = lazyWithReload(() => import("../Pages/CommonPages/BaseUnit/Pages"));
const BrandListing = lazyWithReload(() => import("../Pages/CommonPages/Brand/Pages"));
const CategoryListing = lazyWithReload(() => import("../Pages/CommonPages/Category/Pages"));
const ColourListing = lazyWithReload(() => import("../Pages/CommonPages/Colour/Pages"));
const CompanyListing = lazyWithReload(() => import("../Pages/CommonPages/Company/Pages"));
const DivisionListing = lazyWithReload(() => import("../Pages/CommonPages/Division/Pages"));
const FlavourListing = lazyWithReload(() => import("../Pages/CommonPages/Flavour/Pages"));
const GroupsListing = lazyWithReload(() => import("../Pages/CommonPages/Groups/Pages"));
const TypeListing = lazyWithReload(() => import("../Pages/CommonPages/ProductType/Pages"));
const ShapeListing = lazyWithReload(() => import("../Pages/CommonPages/Shape/Pages"));
const CustomerTypeListing = lazyWithReload(() => import("../Pages/CommonPages/CustomerType/Pages"));
const PaymentTypeListing = lazyWithReload(() => import("../Pages/CommonPages/PaymentType/Pages"));
const PaymentModeListing = lazyWithReload(() => import("../Pages/CommonPages/PaymentMode/Pages"));

// Additional Costs & Charges
const AdditionalChargeListing = lazyWithReload(() => import("../Pages/AdditionalCost/AdditionalCharge/Page"));
const GrnChargeStatusListing = lazyWithReload(() => import("../Pages/AdditionalCost/GrnChargeStatus/Page"));
const GrnAdditionalCostListing = lazyWithReload(() => import("../Pages/AdditionalCost/GrnAdditionalCost/Page"));
// stock management pages
const StockAnalysisListing = lazyWithReload(() => import("../Pages/Stock/StockAnalysis/Page"));

// api logs

const ApiLogsListing = lazyWithReload(() => import("../Pages/ApiLogs/Page"));

// ledger
const LedgerListing = lazyWithReload(() => import("../Pages/AccountingReport/Ledger/Page"));
const ProductLedgerListing = lazyWithReload(() => import("../Pages/AccountingReport/Product/Page"));

// stock management pages
const ErrorFallback = ({ error }) => {
  return (
    <div role="alert">
      <p>Something went wrong:</p>
      <pre style={{ color: "red" }}>{error.message}</pre>
    </div>
  );
};

const PrivateRoute = () => {
  // const { isSetupDone } = useSelector((state) => state.auth);
  const isSetupDone = true;

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <Suspense fallback={<div>Loading...</div>}>
        {isSetupDone ? (
          <Switch>
            <ProtectedRoute exact path="/" component={Dashboard} />
            <ProtectedRoute exact path="/change-password" component={ChangePassword} />
            <ProtectedRoute path="/organization-setup" component={Organization} />
            <ProtectedRoute path="/branch-permission" component={Permission} />
            <ProtectedRoute path="/role" component={Role} />
            <ProtectedRoute path="/branch-role" component={BranchRole} />
            <ProtectedRoute path="/department" component={Department} />
            <ProtectedRoute path="/branch" component={BranchListing} />
            <ProtectedRoute path="/country" component={CountryListing} />
            <ProtectedRoute path="/base-document-rule" component={BaseDocRuleListing} />
            <ProtectedRoute path="/currency" component={CurrencyListing} />
            <ProtectedRoute path="/province" component={ProvinceListing} />
            <ProtectedRoute path="/district" component={DistrictLisitng} />
            <ProtectedRoute path="/palika" component={PalikaListing} />
            <ProtectedRoute path="/ward" component={WardListing} />
            <ProtectedRoute path="/area" component={AreaListing} />
            <ProtectedRoute path="/document-numbering" component={DocumentNumberingListing} />
            <ProtectedRoute path="/document-series" component={DocumentSeriesListing} />
            <ProtectedRoute path="/freight" component={FreightListing} />
            {/* user setup */}
            <ProtectedRoute exact path="/user" component={UserListing} />
            <ProtectedRoute exact path="/user-group" component={UserGroupListing} />
            {/* item Management */}
            <ProtectedRoute exact path="/item" component={ItemListing} />
            {/* party management */}
            <ProtectedRoute exact path="/dimension" component={DimensionListing} />
            <ProtectedRoute exact path="/cost-center-type" component={CostCenterTypeListing} />
            <ProtectedRoute exact path="/cost-center" component={CostCenterListing} />
            {/* party management */}
            <ProtectedRoute exact path="/customer" component={CustomerListing} />

            <ProtectedRoute exact path="/party" component={PartyListing} />
            {/* Accounting System */}
            <ProtectedRoute exact path="/voucher-type" component={VoucherTypeListing} />
            <ProtectedRoute exact path="/gl-account" component={GLAccountListing} />
            <ProtectedRoute exact path="/account-group" component={AccountGroupListing} />
            <ProtectedRoute exact path="/voucher" component={VoucherListing} />
            <ProtectedRoute exact path="/payment-voucher" component={PaymentVoucherListing} />
            <ProtectedRoute exact path="/recipt-voucher" component={ReciptVoucherListing} />
            <ProtectedRoute exact path="/cost-center-report" component={CostCenterReportListing} />
            {/* purchase management */}
            <ProtectedRoute exact path="/material-request" component={MaterialRequest} />
            {/* // TODO : Removed for ibiSap */}
            <ProtectedRoute exact path="/purchase-order" component={PurchaseOrder} />
            <ProtectedRoute exact path="/purchase-invoice" component={PurchaseInvoice} />
            <ProtectedRoute exact path="/purchase-order-branch" component={PurchaseOrderBranchListing} />
            <ProtectedRoute exact path="/pending-invoice-branch" component={PurchaseInvoiceBranchListing} />
            <ProtectedRoute exact path="/purchase-return-branch" component={PurchaseReturnBranchListing} />
            <ProtectedRoute exact path="/purchase-return" component={PurchaseReturnListing} />
            <ProtectedRoute exact path="/grn" component={GRNListing} />
            <ProtectedRoute exact path="/grn-invoice" component={GrnInvoiceListing} />
            <ProtectedRoute exact path="/purchase-debit-note" component={PurchaseDebitNoteListing} />
            <ProtectedRoute exact path="/purchase-credit-note" component={PurchaseCreditNoteListing} />
            {/* sale management */}
            <ProtectedRoute exact path="/sale-request" component={SaleOrderRequestListing} />
            <ProtectedRoute exact path="/sale-order-branch" component={SaleOrderBranchListing} />
            <ProtectedRoute exact path="/sale-return-branch" component={SaleReturnBranchListing} />
            {/* <ProtectedRoute exact path="/sale-order-branch" component={SaleInvoiceBranchListing} /> */}

            <ProtectedRoute exact path="/sale-order" component={SaleOrderListing} />
            <ProtectedRoute exact path="/delivery-note" component={DeliveryNoteListing} />
            <ProtectedRoute exact path="/sale-return" component={SaleReturnListing} />
            <ProtectedRoute exact path="/sale-invoice" component={SaleInoviceListing} />
            <ProtectedRoute exact path="/sale" component={SaleListing} />
            <ProtectedRoute exact path="/sale-debit-note" component={SaleDebitNoteListing} />
            <ProtectedRoute exact path="/sale-credit-note" component={SaleCreditNoteListing} />
            {/* product common pages */}
            <ProtectedRoute exact path="/product/base-unit" component={BaseUnitListing} />
            <ProtectedRoute exact path="/product/brand" component={BrandListing} />
            <ProtectedRoute exact path="/product/category" component={CategoryListing} />
            <ProtectedRoute exact path="/product/colour" component={ColourListing} />
            <ProtectedRoute exact path="/product/company" component={CompanyListing} />
            <ProtectedRoute exact path="/product/division" component={DivisionListing} />
            <ProtectedRoute exact path="/product/flavour" component={FlavourListing} />
            <ProtectedRoute exact path="/product/groups" component={GroupsListing} />
            <ProtectedRoute exact path="/product/type" component={TypeListing} />
            <ProtectedRoute exact path="/product/shape" component={ShapeListing} />
            <ProtectedRoute exact path="/customer/customer-type" component={CustomerTypeListing} />
            <ProtectedRoute exact path="/customer/payment-type" component={PaymentTypeListing} />
            <ProtectedRoute exact path="/customer/payment-mode" component={PaymentModeListing} />
            {/* Additional Costs & Charges */}
            <ProtectedRoute exact path="/grn-additional-cost" component={GrnAdditionalCostListing} />
            <ProtectedRoute exact path="/charge-status" component={GrnChargeStatusListing} />
            <ProtectedRoute exact path="/additional-charge" component={AdditionalChargeListing} />
            {/* Stock Management  */}
            <ProtectedRoute exact path="/stock-analysis" component={StockAnalysisListing} />
            {/* Api logs */}
            <ProtectedRoute exact path="/api-logs" component={ApiLogsListing} />
            <ProtectedRoute exact path="/account-ledger-report" component={LedgerListing} />
            <ProtectedRoute exact path="/product-ledger-report" component={ProductLedgerListing} />
            <Route path="*" component={PageNotFound} />
          </Switch>
        ) : (
          // <OrganizationSetupAlert />
          <Switch>
            <ProtectedRoute path="/organization-setup" component={Organization} />
            <Route path="*" component={OrganizationSetupAlert} />
          </Switch>
        )}
      </Suspense>
    </ErrorBoundary>
  );
};

export default withRouter(PrivateRoute);
